import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { ToastContainer, ToastData, ToastType } from '../components/ui/Toast';

// =============================================================================
// TYPES
// =============================================================================

interface ToastContextType {
  /**
   * Add a toast notification
   * @param message - The message to display
   * @param type - The type of toast (success, error, warning, info)
   * @param duration - Optional duration in ms (default: 5000, 0 for no auto-dismiss)
   * @returns The toast ID for manual removal
   */
  addToast: (message: string, type: ToastType, duration?: number) => string;

  /**
   * Remove a toast by ID
   * @param id - The toast ID to remove
   */
  removeToast: (id: string) => void;

  /**
   * Remove all toasts
   */
  clearToasts: () => void;
}

// =============================================================================
// CONTEXT
// =============================================================================

const ToastContext = createContext<ToastContextType | null>(null);

/**
 * Hook to access toast notifications
 * @throws Error if used outside of ToastProvider
 */
export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

// =============================================================================
// PROVIDER
// =============================================================================

interface ToastProviderProps {
  children: ReactNode;
}

// Default auto-dismiss duration (5 seconds)
const DEFAULT_DURATION = 5000;

// Generate unique IDs for toasts
let toastIdCounter = 0;
const generateToastId = (): string => {
  toastIdCounter += 1;
  return `toast-${Date.now()}-${toastIdCounter}`;
};

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastData[]>([]);

  const addToast = useCallback((message: string, type: ToastType, duration?: number): string => {
    const id = generateToastId();
    const newToast: ToastData = {
      id,
      message,
      type,
      duration: duration !== undefined ? duration : DEFAULT_DURATION,
    };

    setToasts((prevToasts) => [...prevToasts, newToast]);

    return id;
  }, []);

  const removeToast = useCallback((id: string): void => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
  }, []);

  const clearToasts = useCallback((): void => {
    setToasts([]);
  }, []);

  const value: ToastContextType = {
    addToast,
    removeToast,
    clearToasts,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
};

export default ToastContext;
